<template>
  <div class="home">
    <!-- Hero -->
    <section class="hero">
      <div class="container">
        <div class="hero-head">
          <span class="hero-badge">
            开源免费 · 类 LocalSend 的公网版本，可不在局域网内进行传输
          </span>
          <h1 class="hero-title">设备之间,<span class="text-gradient">天涯若比邻</span></h1>
          <p class="hero-desc">
            cloudSend 通过公网中继实现跨网络的设备发现、文件互传、即时消息与剪贴板同步。
            聊天仅存本地,文件直落你的下载目录,服务器零存储。
          </p>
          <div class="hero-actions">
            <router-link to="/download" class="btn btn-primary">立即下载</router-link>
            <a class="btn btn-ghost" href="https://github.com/dellevin/cloudServer" target="_blank" rel="noopener">GitHub 源码</a>
          </div>
          <div class="hero-meta">
            <span class="hero-meta-item" v-for="m in heroMeta" :key="m">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              {{ m }}
            </span>
          </div>
        </div>
        <div class="hero-shots">
          <div class="shot" v-for="(s, i) in shots" :key="i" :class="'shot-' + i">
            <img :src="s" :alt="'cloudSend 应用截图 ' + (i + 1)" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <!-- Fact band -->
    <section class="fact-band">
      <div class="container fact-row">
        <div class="fact" v-for="f in facts" :key="f.label">
          <div class="fact-value">{{ f.value }}</div>
          <div class="fact-label">{{ f.label }}</div>
        </div>
      </div>
    </section>

    <!-- Highlights -->
    <section class="section">
      <div class="container">
        <div style="text-align: center;">
          <span class="section-tag">核心能力</span>
          <h2 class="section-title">一个应用,打通你的所有设备</h2>
          <p class="section-subtitle">无论设备在隔壁房间还是千里之外,填入同一个服务器地址即可互相发现。</p>
        </div>
        <div class="highlight-grid">
          <div class="highlight-card" v-for="item in highlights" :key="item.title">
            <div class="highlight-icon icon" v-html="item.icon"></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How it works -->
    <section class="section section-alt">
      <div class="container">
        <div style="text-align: center;">
          <span class="section-tag">工作原理</span>
          <h2 class="section-title">三条链路,自动择优</h2>
          <p class="section-subtitle">从局域网到公网,cloudSend 自动为你挑选最快的那条路。</p>
        </div>
        <div class="link-flow">
          <template v-for="(p, i) in paths">
            <div class="link-step" :key="p.title">
              <span class="link-step-badge">{{ p.badge }}</span>
              <h3>{{ p.title }}</h3>
              <p>{{ p.desc }}</p>
            </div>
            <div class="link-arrow" v-if="i < paths.length - 1" :key="'arrow-' + i">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- Privacy -->
    <section class="section">
      <div class="container privacy-layout">
        <div class="privacy-text">
          <span class="section-tag">隐私安全</span>
          <h2 class="section-title">你的数据,只属于你</h2>
          <p class="privacy-desc">
            聊天记录只保存在本地 SQLite,文件只落在接收设备的下载目录,
            中继服务器做纯流量中转,不做任何存储。
          </p>
          <router-link to="/guide" class="btn btn-ghost">查看加密细节</router-link>
        </div>
        <div class="privacy-points">
          <div class="privacy-point" v-for="pt in privacyPoints" :key="pt.title">
            <div class="privacy-point-icon icon" v-html="pt.icon"></div>
            <div>
              <h3>{{ pt.title }}</h3>
              <p>{{ pt.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Server notice -->
    <section class="section section-alt server-section">
      <div class="container server-box">
        <div class="server-icon icon" v-html="serverIcon"></div>
        <div class="server-body">
          <h2>跨网互联需要一台公网服务器</h2>
          <p>
            cloudSend 的远程互传依赖你自己部署的公网中继(任意 Linux/Windows 服务器,Python 3.10+ 即可)。
            暂时没有服务器,或愿意赞助服务器费用使用作者维护的公共中继,都可以
            <a href="https://www.iletter.top/" target="_blank" rel="noopener">联系作者</a>。
          </p>
        </div>
        <router-link to="/guide" class="btn btn-ghost server-btn">部署指南</router-link>
      </div>
    </section>

    <!-- CTA -->
    <section class="section cta-section">
      <div class="container" style="text-align: center;">
        <h2 class="section-title">准备好连接你的设备了吗?</h2>
        <p class="section-subtitle">Windows 与 Android 客户端,配上一个轻量 Python 中继,几分钟即可跑起来。</p>
        <router-link to="/download" class="btn btn-primary">下载与部署</router-link>
      </div>
    </section>
  </div>
</template>

<script>
const svg = (inner) =>
  `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`

const shotFiles = [
  'Screenshot_1789959006.png',
  'Screenshot_1789959009.png',
  'Screenshot_1789959021.png',
  'Screenshot_1789959031.png',
  'Screenshot_1789959040.png'
]

export default {
  name: 'HomePage',
  data() {
    return {
      shots: shotFiles.map(f => require(`../assets/app_screen/${f}`)),
      heroMeta: ['E2EE 端到端加密', '服务器零存储', '多链路自动选择'],
      facts: [
        { value: 'v5', label: '传输协议版本' },
        { value: 'AES-256-GCM', label: '端到端加密' },
        { value: '0', label: '服务器数据存储' },
        { value: '双端', label: 'Windows · Android' }
      ],
      serverIcon: svg('<rect x="2" y="3" width="20" height="7" rx="2"/><rect x="2" y="14" width="20" height="7" rx="2"/><path d="M6 6.5h.01"/><path d="M6 17.5h.01"/>'),
      highlights: [
        { icon: svg('<path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>'), title: '文件互传', desc: '多文件/文件夹批量传输,断点续传,SHA-256 校验,同名同大小秒传,大文件并行分片加速。' },
        { icon: svg('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>'), title: '即时消息', desc: '设备间文字聊天,已读回执、消息撤回、会话内搜索,记录只存本地数据库。' },
        { icon: svg('<rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>'), title: '剪贴板同步', desc: '文本/图片/文件在信任设备间自动同步,可按类型开关、按扩展名黑名单过滤。' },
        { icon: svg('<rect x="2" y="4" width="20" height="13" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/>'), title: '远程浏览', desc: '像文件管理器一样浏览对端设备,图片/视频/zip/Office 在线预览,视频流式播放免下载。' },
        { icon: svg('<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>'), title: '端到端加密', desc: 'PBKDF2 派生 AES-256-GCM 密钥,消息对中继服务器完全不可读。' },
        { icon: svg('<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>'), title: '收藏', desc: '重要的消息与文件一键收藏,聊天式布局随时回看。' }
      ],
      paths: [
        { title: '局域网直连', desc: '同网 UDP 发现 + TCP 直连,无需服务器,跑满内网带宽。', badge: '最快' },
        { title: 'P2P 打洞', desc: '跨网优先点对点直连,数据不过第三方,延迟更低。', badge: '优先' },
        { title: '公网中继', desc: '打洞失败走自建中继转发,纯流量中转不存储。', badge: '兜底' }
      ],
      privacyPoints: [
        { icon: svg('<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>'), title: 'AES-256-GCM', desc: '接入密码经 PBKDF2-HMAC-SHA256 五万轮派生密钥。' },
        { icon: svg('<path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z"/>'), title: '中继不可读', desc: '聊天与信令整体加密为 enc 信封,服务器只按目标路由。' },
        { icon: svg('<path d="M3 6h18"/><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>'), title: '零存储', desc: '服务器不存聊天也不存文件,关库即无痕。' }
      ]
    }
  }
}
</script>

<style scoped>
/* Hero */
.hero {
  position: relative;
  padding: 104px 24px 88px;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(640px 320px at 50% -80px, rgba(7, 193, 96, 0.08), transparent 70%);
  pointer-events: none;
}

.hero-head {
  position: relative;
  text-align: center;
  max-width: 680px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-block;
  font-size: 12.5px;
  font-weight: 550;
  color: var(--color-accent-strong);
  background: var(--color-accent-soft);
  border: 1px solid rgba(7, 193, 96, 0.25);
  border-radius: 999px;
  padding: 5px 14px;
  margin-bottom: 22px;
}

.hero-title {
  font-size: 50px;
  font-weight: 750;
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
}

.hero-desc {
  font-size: 16.5px;
  color: var(--color-text-secondary);
  max-width: 560px;
  margin: 0 auto 30px;
}

.hero-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 22px;
}

.hero-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  flex-wrap: wrap;
}

.hero-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.hero-meta-item svg {
  color: var(--color-accent);
}

/* Hero screenshots */
.hero-shots {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 22px;
  margin-top: 68px;
  padding: 0 12px;
}

.shot {
  flex: 0 1 196px;
  min-width: 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  background: #ffffff;
  box-shadow: var(--shadow-raised);
}

.shot img {
  display: block;
  width: 100%;
  height: auto;
}

.shot-0,
.shot-4 {
  flex-basis: 172px;
  margin-top: 34px;
}

.shot-1,
.shot-3 {
  flex-basis: 184px;
  margin-top: 16px;
}

/* Fact band */
.fact-band {
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-raised);
}

.fact-row {
  display: flex;
  justify-content: center;
}

.fact {
  flex: 1;
  max-width: 240px;
  text-align: center;
  padding: 26px 16px;
}

.fact + .fact {
  border-left: 1px solid var(--color-border);
}

.fact-value {
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.fact-label {
  font-size: 12.5px;
  color: var(--color-text-muted);
  margin-top: 3px;
}

/* Sections */
.section-alt {
  background: var(--color-bg-subtle);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}

/* Highlights */
.highlight-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.highlight-card {
  background: var(--color-bg-raised);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 26px 24px;
  box-shadow: var(--shadow-card);
  transition: border-color 0.15s ease;
}

.highlight-card:hover {
  border-color: rgba(7, 193, 96, 0.45);
}

.highlight-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: var(--color-accent-soft);
  color: var(--color-accent-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.highlight-card h3 {
  font-size: 16px;
  font-weight: 650;
  margin-bottom: 8px;
}

.highlight-card p {
  font-size: 13.5px;
  color: var(--color-text-secondary);
}

/* Link flow */
.link-flow {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  max-width: 960px;
  margin: 0 auto;
}

.link-step {
  flex: 1;
  background: var(--color-bg-raised);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 24px;
  transition: border-color 0.15s ease;
}

.link-step:hover {
  border-color: rgba(7, 193, 96, 0.45);
}

.link-step-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-strong);
  background: var(--color-accent-soft);
  border-radius: 999px;
  padding: 3px 11px;
  margin-bottom: 12px;
}

.link-step h3 {
  font-size: 16px;
  font-weight: 650;
  margin-bottom: 6px;
}

.link-step p {
  font-size: 13.5px;
  color: var(--color-text-secondary);
}

.link-arrow {
  align-self: center;
  color: var(--color-text-muted);
  flex-shrink: 0;
}

/* Privacy */
.privacy-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 72px;
  align-items: center;
}

.privacy-text .section-tag {
  margin-bottom: 12px;
}

.privacy-text .section-title {
  margin-bottom: 14px;
}

.privacy-desc {
  font-size: 15.5px;
  color: var(--color-text-secondary);
  margin-bottom: 26px;
  max-width: 440px;
}

.privacy-points {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.privacy-point {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: var(--color-bg-raised);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: var(--shadow-card);
}

.privacy-point-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--color-accent-soft);
  color: var(--color-accent-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}

.privacy-point h3 {
  font-size: 15px;
  font-weight: 650;
  margin-bottom: 3px;
}

.privacy-point p {
  font-size: 13.5px;
  color: var(--color-text-secondary);
}

/* Server notice */
.server-section {
  padding-top: 56px;
  padding-bottom: 56px;
}

.server-box {
  display: flex;
  align-items: center;
  gap: 24px;
  background: var(--color-bg-raised);
  border: 1px solid rgba(7, 193, 96, 0.25);
  border-radius: 14px;
  padding: 28px 32px;
  box-shadow: var(--shadow-card);
}

.server-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 11px;
  background: var(--color-accent-soft);
  color: var(--color-accent-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}

.server-body {
  flex: 1;
}

.server-body h2 {
  font-size: 18px;
  font-weight: 650;
  margin-bottom: 6px;
}

.server-body p {
  font-size: 14px;
  color: var(--color-text-secondary);
  max-width: 720px;
}

.server-body a {
  color: var(--color-accent-strong);
  font-weight: 600;
  text-decoration: none;
}

.server-body a:hover {
  text-decoration: underline;
}

.server-btn {
  flex-shrink: 0;
}

/* CTA */
.cta-section {
  border-top: 1px solid var(--color-border);
}

@media (max-width: 900px) {
  .hero-title {
    font-size: 38px;
  }

  .shot-0,
  .shot-4 {
    display: none;
  }

  .shot-1,
  .shot-3 {
    margin-top: 0;
  }

  .fact-row {
    flex-wrap: wrap;
  }

  .fact {
    flex: 0 0 50%;
    max-width: none;
    padding: 20px 12px;
  }

  .fact:nth-child(3) {
    border-left: none;
  }

  .fact:nth-child(n + 3) {
    border-top: 1px solid var(--color-border);
  }

  .highlight-grid {
    grid-template-columns: 1fr 1fr;
  }

  .link-flow {
    flex-direction: column;
    max-width: 520px;
  }

  .link-arrow {
    transform: rotate(90deg);
  }

  .privacy-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .privacy-desc {
    max-width: none;
  }

  .server-box {
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
  }
}

@media (max-width: 600px) {
  .hero {
    padding-top: 72px;
  }

  .hero-shots {
    gap: 12px;
    margin-top: 48px;
  }

  .highlight-grid {
    grid-template-columns: 1fr;
  }
}
</style>
